/**
 * Shopify-friendly scroll animations
 * - No per-element scroll listeners (1 total)
 * - No dependency on "throttle"
 * - Uses IntersectionObserver + requestAnimationFrame (smooth)
 * - Safe guards for Shopify + theme editor (designMode)
 */

(() => {
  const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
  const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
  const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
  const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

  // ---------- Utilities ----------
  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function percentageSeen(element) {
    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY;

    // Avoid multiple layout passes where possible
    const rect = element.getBoundingClientRect();
    const elementPositionY = rect.top + scrollY;
    const elementHeight = element.offsetHeight;

    if (elementPositionY > scrollY + viewportHeight) return 0;
    if (elementPositionY + elementHeight < scrollY) return 100;

    const distance = scrollY + viewportHeight - elementPositionY;
    const percentage = distance / ((viewportHeight + elementHeight) / 100);
    return clamp(Math.round(percentage), 0, 100);
  }

  // ---------- Scroll-in (fade/slide) ----------
  function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
    const animationEls = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
    if (!animationEls.length) return;

    // Theme editor: show elements without animating (prevents weird editor behavior)
    if (isDesignModeEvent) {
      animationEls.forEach((el) => {
        el.classList.add('scroll-trigger--design-mode');
        el.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry, index) => {
          const el = entry.target;

          if (entry.isIntersecting) {
            if (el.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
              el.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);

              // Cascade ordering (kept from your logic)
              if (el.hasAttribute('data-cascade')) {
                el.style.setProperty('--animation-order', String(index));
              }
            }

            // Animate once then stop observing
            obs.unobserve(el);
          } else {
            // Ensure offscreen state when not visible
            el.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
            el.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
          }
        });
      },
      { rootMargin: '0px 0px -50px 0px' }
    );

    animationEls.forEach((el) => observer.observe(el));
  }

  // ---------- Zoom-in (parallax-ish zoom) ----------
  function initializeScrollZoomAnimationTrigger(rootEl = document) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const zoomEls = Array.from(rootEl.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
    if (!zoomEls.length) return;

    // Keep your original scale amount (very subtle)
    const scaleAmount = 0.2 / 100;

    // Track only visible elements to reduce work
    const visible = new Set();

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      });

      // When visibility changes, update once
      requestTick();
    });

    zoomEls.forEach((el) => {
      observer.observe(el);
      // initial value to avoid flash
      el.style.setProperty('--zoom-in-ratio', String(1));
    });

    let ticking = false;

    function update() {
      ticking = false;
      visible.forEach((el) => {
        const ratio = 1 + scaleAmount * percentageSeen(el);
        el.style.setProperty('--zoom-in-ratio', String(ratio));
      });
    }

    function requestTick() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    // ✅ One scroll listener total
    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick, { passive: true });

    // Initial update
    requestTick();
  }

  // ---------- Shopify section lifecycle ----------
  function initAll(rootEl = document, isDesignModeEvent = false) {
    initializeScrollAnimationTrigger(rootEl, isDesignModeEvent);
    initializeScrollZoomAnimationTrigger(rootEl);
  }

  // First load
  document.addEventListener('DOMContentLoaded', () => initAll(document, false));

  // Shopify theme editor safe hooks
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      initAll(event.target, true);
    });

    document.addEventListener('shopify:section:reorder', () => {
      initAll(document, true);
    });

    // Optional: re-init when merchant changes section settings in editor
    document.addEventListener('shopify:section:select', (event) => {
      initAll(event.target, true);
    });
  }
})();
